import { html, PropertyValueMap } from "lit";
import { customElement, property, state } from 'lit/decorators.js';
import { connect } from 'pwa-helpers';
import { BaseComponent } from "../../features/base-component/base-component";
import store, { waitForCart } from "../../store/store";
import { fetchUserProfile } from "../user-profile/user-profile.thunks";
import { UserProfile } from "../user-profile/user-profile.interface";
import { StoreState } from "../../store/store.interface";
import { selectUser } from "../user-profile/user-profile.selectors";
import { getContinueShoppingUrl, isHEInstructor } from "../../common/utils/cart-util";
import environment, { isLocalEnv } from "../../common/environment";
import { isCartLoadingOrIdle, getCartStoreCatalog, isHeCustomStoreCart, getHeInstructorInvalidProductsRemoved } from "../cart/shop-cart/store/shop-cart.selectors";
import emit from "../../common/events";
import { getShopCart } from "../cart/shop-cart/thunks/get-shop-cart.thunk";
import { mhecommLocalStorage } from "../../common/local-storage";
import { INSTRUCTOR_PRODUCTS_REMOVED_KEY } from "../../common/constants/anayltics.constants";

/**
 * An element to redirect to a target page.
 */
 @customElement('mhecomm-redirect')
 export class RedirectToCustomUrl extends connect(store)(BaseComponent) {

    /**
     *  Redirect url
     */
    @property()
    redirectUrl: string | undefined;

    /**
     *  Redirect url
     */
    @property()
    redirectLabel: string | undefined;

    /**
     *  Redirect url
     */
    @property()
    redirectAction: string | undefined;

    /**
     * The store to checkout to. If not set the national checkout will be used.
     */
    @property()
    store?: string

    @state()
    private user?: UserProfile;

    connectedCallback() {
      super.connectedCallback();
      store.dispatch(fetchUserProfile());
    }
  
    protected async firstUpdated(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>) {
      super.firstUpdated(_changedProperties);
      await store.dispatch(getShopCart({ store: this.store }));
      await waitForCart();
    }
  
    renderComp() {
      if (this.isInstructor()) {
        return html`
          <p hidden>This page will be redirect to : ${this.redirectUrl}</p>`;
      } else {
        if (this.redirectLabel) {
          return html`
            <button class="btn btn-link ps-0" @click=${this._onClick}>${this.redirectLabel}</button>`;
        } else {
          // hidden/unfocusable button for when there is no label
          return html`
            <button class="btn btn-link ps-0 visually-hidden" tabindex="-1" aria-hidden="true" @click=${this._onClick}></button>`;
        }
      }
    }

    stateChanged(state: StoreState) {
      this.user = selectUser(state);
      
      // Guard: Don't redirect until cart load completes
      if (isCartLoadingOrIdle(state)) {
        return;
      }

      if (this.redirectUrl && isHEInstructor(this.user) && !window.Mhecomm_Editor) {
        const shopHostRgx = /www.*\.mheducation\.com/;
        const urlParams = new URLSearchParams(location.search);
        let redirectUrl = new URL(this.redirectUrl);

        if(redirectUrl.host.match(shopHostRgx)) {
          redirectUrl = new URL(this.redirectUrl.replace(shopHostRgx, (new URL(environment().mheducationUrl)).host));
        }

        if (isLocalEnv()) {
          redirectUrl = new URL('/content/mheducation/us/en/checkout/sample.html', new URL(environment().shopUrl));
        }

        urlParams.forEach(function(value, key) {
          redirectUrl.searchParams.set(key, value);
        });

        if (getHeInstructorInvalidProductsRemoved(state)) {
          mhecommLocalStorage.set(INSTRUCTOR_PRODUCTS_REMOVED_KEY, 'true', null);
        }

        if (redirectUrl.pathname != location.pathname) {
          location.href = (redirectUrl).toString();
        }  
      } else if (this.redirectUrl && !isHEInstructor(this.user) && !window.Mhecomm_Editor && this.redirectAction === 'continue-shopping') {
        // TODO: Check for empty cart and generate new redirect url if cart is empty.
        this.redirectUrl = getContinueShoppingUrl(this.store, isHeCustomStoreCart(state), getCartStoreCatalog(state));
      }
    }

    _onClick() {
      if (this.redirectLabel && this.redirectUrl && this.redirectAction === 'continue-shopping') {
        this.emitContinueShopping(this.redirectUrl);
        location.href = this.redirectUrl;
      }
    }

    isInstructor() {
      return this.user && isHEInstructor(this.user);
    }

    private emitContinueShopping(redirectUrl) {
      emit({
        element: this,
        name: 'continue-shopping',
        detail: {
          label: "Continue Shopping",
          href: redirectUrl,
        }
      });
    }
 }

declare global {
    interface HTMLElementTagNameMap {
      'mhecomm-redirect': RedirectToCustomUrl;
    }
  }

